import { useEffect } from 'react';
import { cn } from '../../lib/utils';

interface InventorySlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  description?: string;
  children: React.ReactNode;
  footer: React.ReactNode;
  closeLabel?: string;
  /** Raise the drawer above another slide-over when it is opened as a second layer. */
  zIndexClass?: string;
  widthClassName?: string;
}

export function InventorySlideOver({
  isOpen,
  onClose,
  title,
  subtitle,
  description,
  children,
  footer,
  closeLabel = 'Close',
  zIndexClass = 'z-50',
  widthClassName = 'max-w-[584px]',
}: InventorySlideOverProps) {
  // Lock background page scroll while the drawer is open (same idiom as the
  // cost-price detail modals). Cleanup restores it on close or unmount.
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 bg-black/60 dark:bg-black/60 transition-opacity duration-300',
          zIndexClass,
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
      />

      <div
        className={cn(
          'fixed inset-y-0 right-0 flex flex-col w-full',
          widthClassName,
          zIndexClass,
          'bg-white dark:bg-[#0A2133] shadow-2xl',
          'transform transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center px-4 sm:px-5 py-3 sm:py-4 border-b border-[#E8E8E9] dark:border-[#1E3A4E] flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg border border-[#E8E8E9] dark:border-[#13354F] hover:bg-gray-50 dark:bg-[#13354F] dark:hover:bg-[#1E3A4E] transition-colors"
            aria-label={closeLabel}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="dark:text-[#CBD5E1] text-[#000]" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.8337 10H4.16699" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10.0003 15.8332L4.16699 9.99984L10.0003 4.1665" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-5 pt-3 sm:pt-5 pb-4 flex flex-col min-h-0">
          <h2 className="text-[24px] sm:text-[28px] leading-9 sm:leading-[39px] font-semibold sm:font-bold text-[#161924] dark:text-zinc-50">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-2 sm:mt-4 text-[14px] sm:text-[16px] leading-[21px] font-medium text-[#344054] dark:text-zinc-300">
              {subtitle}
            </p>
          )}
          {description && (
            <p className={cn('text-[14px] sm:text-[16px] leading-[21px] text-[#475467] dark:text-[#CBD5E1]', subtitle ? 'mt-2 sm:mt-4 mb-5 sm:mb-6' : 'mt-2 sm:mt-4 mb-5 sm:mb-6')}>
              {description}
            </p>
          )}
          {!description && !subtitle && <div className="mb-6" />}
          {children}
        </div>

        <div className="flex gap-3 sm:gap-6 px-4 sm:px-5 py-4 sm:py-5 flex-shrink-0">
          {footer}
        </div>
      </div>
    </>
  );
}

export const slideOverCancelClass =
  'flex-1 px-3 h-11 rounded-xl border border-[#D0D5DD] dark:border-[#2D4F68] text-[14px] leading-5 font-semibold text-[#344054] dark:text-[#F8FAFC] hover:bg-gray-50 dark:bg-[#14344C] dark:hover:bg-[#1E3A4E] disabled:opacity-50 transition-colors';

export const slideOverPrimaryClass =
  'flex-1 px-3 h-11 rounded-xl text-[14px] leading-5 font-semibold text-white bg-[#0E8965] hover:bg-[#0E8965]/95 disabled:opacity-60 disabled:cursor-not-allowed transition-colors';

/** Destructive footer action (delete / remove). Same shape as the primary button;
 *  the red is deliberately identical in both themes, like `slideOverPrimaryClass`. */
export const slideOverDangerClass =
  'flex-1 px-3 h-11 rounded-xl text-[14px] leading-5 font-semibold text-white dark:text-white bg-[#e62929] dark:bg-[#e62929] hover:bg-[#d12424] dark:hover:bg-[#d12424] disabled:opacity-60 disabled:cursor-not-allowed transition-colors';
